import { AdServerStroeerSponsorshipSettings } from '@mbrtargeting/metatag-config-types';
import { createHtmlFragment, createIntervalObservable, partition, simpleUrlSwitch } from '@mbrtargeting/metatag-utils';
import { Phase } from '../../../interfaces/constants.js';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onModuleRegistration } from '../../../loader/decorators/on-module-registration.js';
import { onPhase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectAdserverSponsorshipSettings, selectCommonSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { markSdg } from '../../../utils/performance.js';
import { sendCorsRequestPromise } from '../../../utils/send-cors-request.js';
import { GLOBAL_TARGETING, ID_STORE, STORAGE_HELPER } from '../../token.js';
import { setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { constructAdUnitPath } from '../../utils/construct-adunit-path.js';
import { LifecycleAction } from '../adslot-controller/adslot-lifecycle-state.js';
import { Adslot, isAdslotInstance } from '../adslot-controller/adslot.js';
import { CreativeSource, CreativeSourceLoadSlotsParams } from '../adslot-controller/creative-source.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { StorageConsent, StorageHelper, StorageType } from '../storage/storage-helper.js';
import { IdStore } from '../idstore/idstore.js';

const log = logger.logGroup({ prefix: 'StroeerSponsorship' });

@injectionTarget()
export class StroeerSponsorship implements CreativeSource {

    @inject(ADSLOT_REGISTRY) #adslotRegistry!: AdslotRegistry;
    @inject(CONFIG_RESOLVER) #configResolver!: ConfigResolver;
    @inject(CONSENT_CACHE) #consentCache!: ConsentCache;
    @inject(GLOBAL_TARGETING) #globalTargeting!: GlobalTargeting;
    @inject(STORAGE_HELPER) #storageHelper!: StorageHelper;
    @inject(ID_STORE) #idStore!: IdStore;

    /**
     * In-memory storage for campaigns.
     */
    #allStoredCampaigns: Record<string, Record<string, ScheduleItem[]>> = {};

    /**
     * Returns the configuration for the Stroeer Sponsorship module.
     *
     * @returns the configuration object
     */
    getConfig = (): AdServerStroeerSponsorshipSettings => this.#configResolver.get(selectAdserverSponsorshipSettings()) ?? {};

    /**
     * Checks if we have consent or legitimate interest for
     *  - purpose 2 ("Use limited data to select advertising") and
     *  - purpose 7 ("Measure advertising performance")
     * or if GDPR does not apply.
     *
     * **NOTE**: purpose 1 is not included here as it is checked before reading or writing local storage
     *
     * @returns true if we are allowed to proceed, false otherwise
     */
    isAllowed = () => this.#consentCache.ifConsentConditions([
        { consentedPurposes: [2, 7], consentedVendors: [1057] },
        { legitimateInterestPurposes: [2, 7], legitimateInterestVendors: [1057] },
        { gdprApplies: false },
    ]);

    /**
     * Restores campaigns from local storage.
     */
    @onModuleRegistration()
    public restoreCampaigns(): void {
        // restore campaigns from local storage whenever we get consent for purpose 1 ("store and/or access information on a device")
        this.#storageHelper.get(StorageType.LOCAL, StorageConsent.WAIT, 'sdgCampaigns').then(campaignsSerialized => {
            this.#allStoredCampaigns = campaignsSerialized ? JSON.parse(campaignsSerialized) : {};
        });
    }

    /**
     * Delayed and periodic fetch of scheduled campaigns and stores them in memory and local storage.
     */
    @onPhase({ phase: Phase.TARGETING })
    public fetchCampaigns(): void {
        const { active, scheduleUrl, siteId } = this.getConfig();
        const { kvFilter = [] } = this.#configResolver.get(selectCommonSettings()) || {};

        // fetch campaigns immediately and then every 5 minutes for single-page-applications
        createIntervalObservable({ interval: 5 * 6e4, immediate: true }).subscribe(() => {

            // do not run the network request if module is not active
            if (!active || !this.isAllowed()) return;

            const request: ScheduleRequest = {
                websiteId: siteId,
                kvg: this.#globalTargeting.filter(([key]) => !kvFilter.includes(key)).toObject(),
                positions: this.#adslotRegistry.getAdslots().filter(isAdslotInstance).map(slot => ({
                    name: slot.slotName,
                    adUnits: slot.getAdUnits(),
                    kvl: slot.getTargeting().filter(([key]) => !kvFilter.includes(key)).toObject(),
                })),
            };

            sendCorsRequestPromise<Record<string, ScheduleItem[]>>({
                src: scheduleUrl,
                method: 'POST',
                body: JSON.stringify(request),
            }).then(response => {
                this.#allStoredCampaigns[siteId] = response;
                return this.#storageHelper.set(StorageType.LOCAL, StorageConsent.WAIT, 'sdgCampaigns', JSON.stringify(this.#allStoredCampaigns));
            });
        });
    }

    public async loadSlots({ adslots, done }: CreativeSourceLoadSlotsParams): Promise<void> {
        const { active, creativeUrl, siteId } = this.getConfig();
        const timeOverride = simpleUrlSwitch(/sdgHrTime=(\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}Z)/i);
        const isDisabled = !!simpleUrlSwitch(/sdgHr=(disable)/i);
        const isAllowed = this.isAllowed();

        // if the module is not active or a sponsorship is disabled via url, or we have no consent, skip the adslots
        if (!active || isDisabled || !isAllowed) {
            log.debug('skipping slots %o (active=%s, isDisabled=%s, isAllowed=%s)', [adslots.map(s => s.slotName.toUpperCase()).join(','), active, isDisabled, isAllowed]);
            done(adslots);
            return;
        }

        log.debug('calling slots: %o', [adslots.map(s => s.slotName.toUpperCase())]);

        // Remove milliseconds from the date to match the server response
        const now = timeOverride ?? `${new Date().toISOString().slice(0, 19)}Z`;

        // find campaigns per adslot, the resulting map (key=adslot, value=campaign) will only contain adslots that have a campaign
        const adslotCampaignMap = new Map(adslots
            .map(adslot => [adslot, this.findQualifyingCampaign(adslot, now, siteId)] as const)
            .filter((x): x is [Adslot, ScheduleItem] => !!x[1])
        );

        // split the adslots array into two arrays: one with adslots that have campaigns and one without
        const [adslotsWithCampaigns, adslotsWithoutCampaigns] = partition(adslots, adslot => adslotCampaignMap.has(adslot));

        // if there are adslots without campaigns, send them back immediately to be filled by other creative sources
        if (adslotsWithoutCampaigns.length) {
            log.debug('skipping slots %o (no campaigns found)', [adslotsWithoutCampaigns.map(s => s.slotName.toUpperCase()).join(',')]);
            done(adslotsWithoutCampaigns);
        }

        // if there are no adslots with campaigns, we are done - we don't need to send a request to the creative server
        if (!adslotsWithCampaigns.length) return;

        log.debug('slots with campaigns: %o', [adslotsWithCampaigns.map(s => s.slotName.toUpperCase())]);

        // getting consent data - this operation is safe as we have already checked if GDPR should be applied on startup
        const { gdprApplies = false, tcString = '' } = this.#consentCache.getTcData() ?? {};
        // build the request payload for the creative server
        const request: CreativeRequest = {
            wid: siteId,
            gdpr: gdprApplies,
            gdpr_consent: tcString,
            uids: this.#idStore.getIds(),
            positions: Array.from(adslotCampaignMap.entries()).map(([slot, campaign]) => ({
                name: slot.slotName,
                cid: campaign.campaignId,
                crid: campaign.creativeId,
                pt: slot.getPageType(),
                zo1: slot.getZone(),
                adUnits: slot.getAdUnits()
            }))
        };

        await sendCorsRequestPromise<CreativeResponse>({
            src: `${creativeUrl}${!!timeOverride ? '?db=true' : ''}`,
            method: 'POST',
            body: JSON.stringify(request),
        }).then(response => Promise.allSettled(adslotsWithCampaigns.map(async slot => {
            // fill all adslots with the creatives received from the creative server in parallel
            const campaign = adslotCampaignMap.get(slot)!;
            const { ad, width, height, blocker = false } = response[slot.slotName] ?? { ad: '', width: 0, height: 0 };

            log.info(`Injecting sponsorship creative for position ${slot.slotName.toUpperCase()} (blocker=${blocker})`);
            markSdg(`sponsorship_creative_injected_${slot.slotName}`);
            // valid ad found, mark the slot as filled/blocked to prevent other creativeSources from filling it
            await slot.lifecycle.dispatch(blocker ? LifecycleAction.BLOCK_HIDE : LifecycleAction.FILL);

            slot.renderCreative((async () => {
                const targetNode = slot.getStableDivElement({
                    id: `sdgHyperRenderingNode-${slot.slotName}`,
                    emptyOnReload: true,
                });

                // create an iframe via createHtmlFragment to render the ad code and attach it to the targetNode
                const iframe = targetNode.appendChild(createHtmlFragment({
                    html: ad,
                    styles: {
                        display: blocker ? 'none' : 'inline',
                        width: width > 1 ? `${width}px` : '0',
                        height: height > 1 ? `${height}px` : '0',
                    },
                }));

                // attach a DV script inside a stable-element and measure advert viewability.
                if (!blocker) slot.addVisibilityNode({ name: 'hyperRender', node: iframe });
            })());

            // add some ids for the infoTool
            setIdsForAdSlot(slot.slotName, {
                source: 'hyperRender',
                values: {
                    advertiserId: '0',
                    orderId: '0',
                    creativeId: campaign.creativeId,
                    campaignId: campaign.campaignId,
                    lineItemId: '0',
                    isEmpty: false,
                    size: `${width}x${height}`,
                    adUnitPath: '/HyperRender' + constructAdUnitPath(slot, {
                        addNetworkId: false,
                        addMcmId: false,
                    }),
                    redirectCreativeId: '0',
                    redirectLineItemId: '0',
                    deliveryType: 'hr',
                    ...blocker && { formatCode: 'blocker' },
                }
            });
        }))).catch(() => {
            log.warn("Failed to fetch and/or render fast sponsorship creatives for adslots: %s", [adslotsWithCampaigns.map(s => s.slotName.toUpperCase()).join(',')]);
        }).finally(() => {
            log.info("Fast sponsorship creative source finished");
            // tell the adslot controller that we are done with the adslots that have campaigns
            done(adslotsWithCampaigns);
        });
    }

    private findQualifyingCampaign(adslot: Adslot, time: string, siteId: string): ScheduleItem | undefined {
        const campaignsForSite = this.#allStoredCampaigns[siteId] ?? {};
        const qualifying = campaignsForSite[adslot.slotName]?.filter(campaign => {
            return (time >= campaign.start) && (time < campaign.end);
        }) ?? [];

        if (qualifying.length <= 1) return qualifying[0];
        const idx = Math.floor(Math.random() * qualifying.length);
        return qualifying[idx];
    }
}

// Having this here is temporary
interface ScheduleRequest {
    websiteId: string;
    kvg: Record<string, string[]>;
    positions: {
        name: string;
        adUnits: string[];
        kvl: Record<string, string[]>;
    }[];
}

interface ScheduleItem {
    campaignId: string;
    creativeId: string;
    start: string;
    end: string;
}

interface CreativeRequest {
    wid: string;
    gdpr: boolean;
    gdpr_consent: string;
    uids: Record<string, string>;
    positions: {
        name: string; // Position name
        cid: string; // Core campaign ID
        crid: string; // Fast sponsorship creative ID
        pt: string; // Page type
        zo1: string; // Zone
        adUnits: string[];
    }[];
}

interface CreativeResponse {
    [slotName: string]: Creative;
}

interface Creative {
    ad: string;
    width: number;
    height: number;
    blocker?: boolean;
}
